import formatRoute from '@src/components/route/format-route';
import matchPath from '@src/components/route/route-switch-enhance/match-path';
import RouteEnhance from '@src/components/route/route-enhance';

/**
 * 路由配置
 * 参数如 path, exact 等请参考: https://reacttraining.com/react-router/web/example/basic
 * 其中新增以下自定义属性:
 *
 * {Function} asyncRender: 异步 render 函数, 返回 promise component, 如 import('../main'), 用于 webpack split chunks
 * {Function} getFormatPath: 用于生成真正的跳转路径, 如 MAIN 中, 传入 id = 10, 将生成 /10 的路径
 */
const routeConfigs = {
  /**
   * 首页
   * 规则参考: /?a=12312
   */
  MAIN: {
    path: '/',
    // native: true, // 开启后直接使用默认路由, 不进行预加载, loading 和 切换入场动画
    // animate: false, // 禁用页面入场动画(默认右边滑到左边)
    // useLoading: false, // 是否使用 loading, 默认开启. loading 目前为小鸡 gif
    exact: true, // 完全匹配, 与 react-route-dom 行为一直, https://www.jianshu.com/p/e3adc9b5f75c
    asyncRender: () => import(/* webpackChunkName: "page-main", webpackPrefetch: true */ '../main'),
    getFormatPath: () => formatRoute(routeConfigs.MAIN.path)
  },
  /**
   * 降级命中页, 常作为 404 使用
   */
  FALLBACK: {
    path: '**',
    /**
     * 这里使用 "~defer" 代表不进行 prefetch
     * @returns {Promise<*>|*}
     */
    asyncRender: () => import(/* webpackChunkName: "page-not-found~defer" */ '../not-found'),
    getFormatPath: () => formatRoute(routeConfigs.MAIN.path)
  }
};

/**
 * 获取 match 匹配路由
 * @param {string} actualPath - 实际地址, 如: /person/detail?id=123
 * @returns {string|null}
 */
routeConfigs.match = function(actualPath) {
  if (!/^\/[^/].*$/.test(actualPath)) {
    return null;
  }
  const [pathname, query] = actualPath.split('?');
  const matchKey = Object.keys(routeConfigs).find(key => {
    return !!matchPath({ pathname, query }, routeConfigs[key]);
  });
  return routeConfigs[matchKey] || null;
};

/**
 * 禁止遍历
 */
Object.defineProperty(routeConfigs, 'match', { enumerable: false });

/**
 * 预加载, e.g:
 * routeConfigs.prefetch('/person/detail?id=123')
 * routeConfigs.prefetch({ actualPath: '/person/detail?id=123', prefetchUntilLoadAsyncComponent: true })
 *
 * @param {string|{actualPath,prefetchUntilLoadAsyncComponent}} actualPath - 实际地址, 如: /person/detail?id=123
 * @returns {routeConfigs.MAIN.asyncRender|(function(): (Promise<*>|*))|routeConfigs.GALLERY.asyncRender|routeConfigs.FALLBACK.asyncRender|Promise<*>}
 */
routeConfigs.prefetch = actualPath => {
  if (typeof actualPath === 'string') {
    const routeConfig = routeConfigs.match(actualPath);
    if (routeConfig && routeConfig.asyncRender) {
      return RouteEnhance.prefetch({ asyncRender: routeConfig.asyncRender });
    }
    return undefined;
  }
  /**
   * @type {{actualPath,prefetchUntilLoadAsyncComponent}}
   */
  const options = actualPath;
  const routeConfig = routeConfigs.match(options.actualPath);
  if (routeConfig && routeConfig.asyncRender) {
    return RouteEnhance.prefetch({
      asyncRender: routeConfig.asyncRender,
      prefetchUntilLoadAsyncComponent: options.prefetchUntilLoadAsyncComponent
    });
  }
  return undefined;
};

/**
 * 禁止遍历
 */
Object.defineProperty(routeConfigs, 'prefetch', { enumerable: false });

export default routeConfigs;
